import React, { useContext, useMemo, useState } from 'react'
import clsx from 'clsx'
import { GraphQL } from '@guestgoodie/graphql'

import {
  Controller,
  ControllerProps,
  ControllerRenderProps,
  FieldErrors,
  FieldPath,
  FieldValues,
  useFormContext,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger
} from 'react-hook-form'

import Checkbox from '@/components/ui/Checkbox'
import { Error, TranslationError } from './Error'
import { useTranslation } from './Translation'
import { CustomContext } from './CustomProvider'

export type FieldChildrenProps<T extends FieldValues> = {
  errors: FieldErrors<T>
  trigger: UseFormTrigger<T>
  setValue: UseFormSetValue<T>
  getValues: UseFormGetValues<T>
  locale: GraphQL.AppLocale
  isDefaultLocale: boolean
  renderProps: ControllerRenderProps & {
    invalid: boolean
    readOnly: boolean
  }
}

type Props<T extends FieldValues, TName extends FieldPath<T> = FieldPath<T>> = {
  name: TName
  label?: string
  description?: string
  size?: 'regular' | 'small'
  isInitialEnabled?: boolean
  canBeDisabled?: boolean
  rules?: ControllerProps<T, TName>['rules']
  disabled?: boolean
  resetOnUnmount?: boolean
  onEnabledChange?: (isEnabled: boolean) => void
  children: (props: FieldChildrenProps<T>) => React.ReactElement
} & FieldValues

export function Field<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>
>(props: Props<T, TName>) {
  const {
    hasTranslations = false,
    isTranslatable,
    locale,
    locales,
    defaultLocale
  } = useTranslation() ?? {}

  const shouldTranslate = useMemo(
    () => isTranslatable?.(props.name) ?? false,
    [props.name, isTranslatable]
  )

  if (!hasTranslations || !shouldTranslate) {
    return (
      <DefaultField
        {...props}
        locale={locale ?? defaultLocale}
        isDefaultLocale={defaultLocale === locale}
      />
    )
  }

  return (
    <>
      {locales?.map(function (loc) {
        const name =
          defaultLocale === loc
            ? props.name
            : (`translations.${loc}.${props.name}` as TName)

        return (
          <div
            className={clsx(loc === locale ? 'block flex-1' : 'hidden')}
            key={name}
          >
            <DefaultField
              {...props}
              locale={locale}
              isDefaultLocale={defaultLocale === locale}
              name={name}
            />
          </div>
        )
      })}
    </>
  )
}

function DefaultField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>
>(props: Props<T, TName>) {
  const {
    children,
    name,
    canBeDisabled = false,
    disabled = false,
    isInitialEnabled = true,
    label,
    description,
    rules,
    size = 'regular',
    locale,
    isDefaultLocale,
    resetOnUnmount = false,
    onEnabledChange
  } = props

  const { control, trigger, setValue, getValues } = useFormContext<T>()
  const { readOnly, disabled: rootDisabled } = useContext(CustomContext)
  const [isEnabled, setIsEnabled] = useState(isInitialEnabled)

  return (
    <fieldset
      className={clsx(
        'flex min-w-0 flex-1 flex-col',
        size === 'regular' && 'gap-2.5',
        size === 'small' && 'gap-1'
      )}
    >
      <Controller<T, TName>
        name={name}
        disabled={disabled}
        control={control}
        rules={rules}
        shouldUnregister={resetOnUnmount}
        render={({
          field: { value, onChange, disabled, ...rest },
          formState: { errors }
        }) => {
          return (
            <>
              {label && (
                <Label
                  name={name}
                  canBeDisabled={canBeDisabled}
                  isEnabled={isEnabled}
                  label={label}
                  size={size}
                  onEnabled={isEnabled => {
                    setIsEnabled(isEnabled)
                    onEnabledChange?.(isEnabled)
                  }}
                />
              )}
              {description && (
                <span className="text-regular text-text">{description}</span>
              )}
              <div className="flex flex-col gap-1">
                {isEnabled &&
                  children({
                    errors,
                    trigger,
                    setValue,
                    getValues,
                    locale,
                    isDefaultLocale,
                    renderProps: {
                      ...rest,
                      value,
                      onChange,
                      invalid: errors[name] ? true : false,
                      readOnly,
                      disabled: rootDisabled || disabled
                    }
                  })}
                <Error name={name} />
                <TranslationError name={name} locale={locale} />
              </div>
            </>
          )
        }}
      />
    </fieldset>
  )
}

interface LabelProps {
  label: string
  name: string
  isEnabled: boolean
  canBeDisabled: boolean
  size?: 'regular' | 'small'
  onEnabled: (isEnabled: boolean) => void
}

function Label(props: LabelProps) {
  const { name, label, isEnabled, canBeDisabled, onEnabled, size } = props

  return (
    <div className="flex flex-row items-center gap-2">
      {canBeDisabled && (
        <Checkbox
          checked={isEnabled}
          onChange={() => onEnabled(!isEnabled)}
          className={clsx(
            'text-text',
            size === 'regular' && 'text-heading3',
            size === 'small' && 'text-regular font-bold'
          )}
        >
          {label}
        </Checkbox>
      )}
      {!canBeDisabled && (
        <label
          className={clsx(
            'text-text',
            size === 'regular' && 'text-heading3',
            size === 'small' && 'text-regular font-bold'
          )}
          htmlFor={name}
        >
          {label}
        </label>
      )}
    </div>
  )
}
